/**
 * Call Types and Interfaces
 * Based on Assistent.ai API and Design Requirements
 */

export interface Call {
    sessionId: string
    startTime: string // ISO datetime
    endTime: string // ISO datetime
    callerName?: string
    callerPhone?: string
    bookingTime?: string // ISO datetime
    guestCount?: number
    bookingId?: string
    actions: CallAction[]
    reviewed: boolean
}

export interface CallAction {
    type: string
    timestamp: string
    data?: any
}

// Call outcome categories based on API conversation states
export type CallOutcome =
    | 'BUSINESSDATA_DONE'
    | 'BUSINESSDATA_NOTCOMPLETED'
    | 'CONVERSATION_WITHOUT_BUSINESSDATA'
    | 'CALL_FORWARDED'
    | 'HANGUP_DIRECTLY'
    | 'OTHER'
    | 'UNKNOWN'

export interface CallCategory {
    id: CallOutcome
    label: string
    count: number
    actions: string[]
}

// Result status for visual indicators
export type CallResult =
    | 'SUCCESS' // ✓
    | 'FAILURE' // ✗
    | 'SMS_SENT' // 📧
    | 'FORWARDED' // 📞
    | 'OTHER' // Phone icon for general requests
    | 'CANCELLATION' // Edit/cancel icon
    | 'UNKNOWN' // ?

export interface CallFilters {
    timeperiod: 'today' | 'yesterday' | 'last7days' | 'currentMonth' | 'custom'
    startDate?: Date
    endDate?: Date
    category: CallOutcome | 'all'
    searchQuery: string
    showReviewed: boolean
}

// Direct mapping from API conversation states
export const ACTION_CATEGORIES: Record<CallOutcome, string[]> = {
    BUSINESSDATA_DONE: [
        'BUSINESSDATA_DONE'
    ],
    BUSINESSDATA_NOTCOMPLETED: [
        'BUSINESSDATA_NOTCOMPLETED'
    ],
    CONVERSATION_WITHOUT_BUSINESSDATA: [
        'CONVERSATION_WITHOUT_BUSINESSDATA',
        'FAQ',
        'SMALLTALK',
        'GLOBAL'
    ],
    CALL_FORWARDED: [
        'CALL_FORWARDED',
        'FORWARD_TRY',
        'CALLFORWARD_ACTIVE',
        'CALLFORWARD_INACTIVE'
    ],
    HANGUP_DIRECTLY: [
        'HANGUP_DIRECTLY'
    ],
    OTHER: [],
    UNKNOWN: []
}

// Helper function to determine call outcome from actions
export function determineCallOutcome(actions: CallAction[]): CallOutcome {
    if (!actions || actions.length === 0) return 'UNKNOWN'

    const actionTypes = actions.map(a => a.type)

    // Check each category
    for (const [outcome, outcomeActions] of Object.entries(ACTION_CATEGORIES)) {
        if (outcomeActions.some(action => actionTypes.includes(action))) {
            return outcome as CallOutcome
        }
    }

    return 'UNKNOWN'
}

// Helper function to determine result status for visual indicator
export function determineCallResult(call: Call): CallResult {
    const outcome = determineCallOutcome(call.actions)

    switch (outcome) {
        case 'BUSINESSDATA_DONE':
            return 'SUCCESS'
        case 'CALL_FORWARDED':
            return 'FORWARDED'
        case 'BUSINESSDATA_NOTCOMPLETED':
            return 'FAILURE'
        case 'CONVERSATION_WITHOUT_BUSINESSDATA':
        case 'OTHER':
            return 'OTHER'
        case 'HANGUP_DIRECTLY':
            return 'UNKNOWN'
        default:
            return 'UNKNOWN'
    }
}

// Check if SMS was sent during call
export function hasSmsAction(actions: CallAction[]): boolean {
    return actions.some(a => a.type.includes('SMS') || a.type.includes('MESSAGE'))
}
